import { useCallback, useEffect, useRef, useState } from "react";
import { Bot, Check, Filter, Loader2, Mail, MessageSquare, Plus, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAiChat } from "@/api/hooks";
import {
    mailApi,
    type AiFilterSuggestion,
    type AiComposeSuggestion
} from "@/api/mailApi";
import { useAccessToken } from "@/auth/useAccessToken";
import type { ComposeSeed } from "@/lib/composeDraft";
import { sanitizeHtml } from "@/lib/sanitizeHtml";

type Mode = "general" | "filter" | "compose" | "reply";

interface SelectedEmail {
    id: string;
    subject: string;
    fromName: string;
    fromEmail: string;
    bodyText: string;
}

interface ChatMessage {
    role: "user" | "assistant";
    content: string;
    filter?: AiFilterSuggestion;
    compose?: AiComposeSuggestion;
}

interface Props {
    onClose: () => void;
}

export function AiChatPanel({ onClose }: Props) {
    const token = useAccessToken();
    const aiChat = useAiChat();
    const [mode, setMode] = useState<Mode>("general");
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState("");
    const [selectedEmail, setSelectedEmail] = useState<SelectedEmail | null>(null);
    const [savedFilters, setSavedFilters] = useState<Set<number>>(new Set());
    const [savingFilter, setSavingFilter] = useState<number | null>(null);
    const [openedCompose, setOpenedCompose] = useState<Set<number>>(new Set());
    const scrollRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    // Keep the current reading-pane email in sync — MailPage broadcasts it
    useEffect(() => {
        const handler = (e: Event) => {
            const msg = (e as CustomEvent<{ message: SelectedEmail }>).detail.message;
            setSelectedEmail(msg);
        };
        window.addEventListener("mail:message-focused", handler as EventListener);
        return () => window.removeEventListener("mail:message-focused", handler as EventListener);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages, aiChat.isPending]);

    const send = useCallback(async () => {
        const text = input.trim();
        if (!text || aiChat.isPending) return;

        const userMsg: ChatMessage = { role: "user", content: text };
        const history = [...messages, userMsg];
        setMessages(history);
        setInput("");

        const emailContext =
            mode === "reply" && selectedEmail
                ? {
                      subject: selectedEmail.subject,
                      fromName: selectedEmail.fromName,
                      fromEmail: selectedEmail.fromEmail,
                      bodyText: selectedEmail.bodyText
                  }
                : undefined;

        try {
            const resp = await aiChat.mutateAsync({
                mode,
                messages: history.map((m) => ({ role: m.role, content: m.content })),
                emailContext
            });
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content: resp.reply,
                    filter: resp.generatedFilter,
                    compose: resp.composeSuggestion
                }
            ]);
        } catch {
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content:
                        "Sorry, I couldn't process that. Please check the Anthropic API key is configured in the BFF."
                }
            ]);
        }
    }, [input, messages, mode, selectedEmail, aiChat]);

    const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void send();
        }
    };

    const handleCreateFilter = async (filter: AiFilterSuggestion, idx: number) => {
        setSavingFilter(idx);
        try {
            await mailApi.createFilter(
                {
                    name: filter.name,
                    isEnabled: true,
                    sortOrder: 0,
                    conditionsJson: filter.conditionsJson,
                    actionsJson: filter.actionsJson
                },
                token
            );
            setSavedFilters((prev) => new Set(prev).add(idx));
        } catch {
            // no-op; toast not wired here
        } finally {
            setSavingFilter(null);
        }
    };

    const handleOpenCompose = (compose: AiComposeSuggestion, idx: number) => {
        const seed: Partial<ComposeSeed> = {
            kind: mode === "reply" && selectedEmail ? "Reply" : "New",
            to: mode === "reply" && selectedEmail ? selectedEmail.fromEmail : "",
            subject: compose.subject ?? "",
            bodyHtml: compose.bodyHtml ?? "<p></p>",
            inReplyToMessageId: null,
            cc: "",
            bcc: "",
            identityId: null,
            attachmentIds: []
        };
        window.dispatchEvent(new CustomEvent("ai:open-compose", { detail: { seed } }));
        setOpenedCompose((prev) => new Set(prev).add(idx));
    };

    const clearChat = () => {
        setMessages([]);
        setSavedFilters(new Set());
        setOpenedCompose(new Set());
    };

    const MODES: { id: Mode; label: string; icon: React.ReactNode }[] = [
        { id: "general", label: "Chat",    icon: <MessageSquare className="h-3.5 w-3.5" /> },
        { id: "filter",  label: "Filter",  icon: <Filter className="h-3.5 w-3.5" /> },
        { id: "compose", label: "Compose", icon: <Mail className="h-3.5 w-3.5" /> },
        { id: "reply",   label: "Reply",   icon: <Bot className="h-3.5 w-3.5" /> }
    ];

    return (
        <div
            role="dialog"
            aria-label="Athavita AI"
            className="fixed bottom-4 right-4 z-40 flex w-96 flex-col rounded-xl border bg-background shadow-2xl"
            style={{ height: 560 }}
        >
            {/* Header */}
            <div className="flex items-center gap-2 rounded-t-xl bg-primary px-3 py-2 text-primary-foreground">
                <Bot className="h-5 w-5 shrink-0" />
                <span className="flex-1 text-sm font-semibold">Athavita AI</span>
                <button
                    type="button"
                    onClick={clearChat}
                    className="rounded px-1.5 py-0.5 text-xs opacity-70 hover:opacity-100 hover:bg-white/20"
                    title="Clear conversation"
                >
                    Clear
                </button>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-primary-foreground hover:bg-white/20"
                    onClick={onClose}
                    aria-label="Close AI panel"
                >
                    <X className="h-4 w-4" />
                </Button>
            </div>

            {/* Mode tabs */}
            <div className="flex border-b bg-muted/30 px-2 py-1 gap-1 shrink-0">
                {MODES.map((m) => (
                    <button
                        key={m.id}
                        type="button"
                        onClick={() => setMode(m.id)}
                        className={`flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                            mode === m.id
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        }`}
                    >
                        {m.icon}
                        {m.label}
                    </button>
                ))}
            </div>

            {/* Reply context banner */}
            {mode === "reply" && (
                <div
                    className={`shrink-0 border-b px-3 py-1.5 text-xs ${
                        selectedEmail
                            ? "bg-amber-50 text-amber-800 dark:bg-amber-950/20 dark:text-amber-200"
                            : "bg-muted/30 text-muted-foreground"
                    }`}
                >
                    {selectedEmail ? (
                        <>
                            <span className="font-medium">Context:</span>{" "}
                            {selectedEmail.subject} — from {selectedEmail.fromName}
                        </>
                    ) : (
                        "Open an email first, then ask me to draft a reply."
                    )}
                </div>
            )}

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3 p-3">
                {messages.length === 0 && !aiChat.isPending && (
                    <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-muted-foreground">
                        <Bot className="h-10 w-10 opacity-20" />
                        <p className="text-sm">
                            {mode === "filter" &&
                                'Describe a filter rule — e.g. "Move newsletters to a Newsletters folder"'}
                            {mode === "compose" &&
                                'Describe the email you want to write — e.g. "Follow up on yesterday\'s meeting"'}
                            {mode === "reply" &&
                                'Open an email and tell me how to reply — e.g. "Decline politely, suggest next week"'}
                            {mode === "general" && "Ask me anything about your email or the Athavita suite."}
                        </p>
                    </div>
                )}

                {messages.map((msg, idx) => (
                    <div
                        key={idx}
                        className={`flex flex-col gap-1.5 ${msg.role === "user" ? "items-end" : "items-start"}`}
                    >
                        {/* Bubble */}
                        <div
                            className={`max-w-[85%] whitespace-pre-wrap rounded-xl px-3 py-2 text-sm leading-relaxed ${
                                msg.role === "user"
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted text-foreground"
                            }`}
                        >
                            {bubbleText(msg)}
                        </div>

                        {/* Filter suggestion card */}
                        {msg.filter ? (
                            <FilterCard
                                filter={msg.filter}
                                saved={savedFilters.has(idx)}
                                saving={savingFilter === idx}
                                onSave={() => void handleCreateFilter(msg.filter!, idx)}
                            />
                        ) : null}

                        {/* Compose suggestion card */}
                        {msg.compose ? (
                            <ComposeCard
                                compose={msg.compose}
                                opened={openedCompose.has(idx)}
                                onOpen={() => handleOpenCompose(msg.compose!, idx)}
                            />
                        ) : null}
                    </div>
                ))}

                {aiChat.isPending && (
                    <div className="flex items-start">
                        <div className="flex items-center gap-2 rounded-xl bg-muted px-3 py-2 text-sm text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Thinking…
                        </div>
                    </div>
                )}
            </div>

            {/* Input */}
            <div className="shrink-0 border-t p-2 flex gap-2 items-end">
                <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKey}
                    disabled={aiChat.isPending}
                    rows={2}
                    placeholder={
                        mode === "filter"
                            ? "e.g. Move newsletters from Substack to Junk"
                            : mode === "compose"
                              ? "e.g. Write a thank-you for yesterday's demo"
                              : mode === "reply"
                                ? "e.g. Decline politely, suggest next week"
                                : "Ask anything…"
                    }
                    className="flex-1 min-h-[56px] max-h-[120px] resize-none rounded-md border bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
                />
                <Button
                    size="icon"
                    disabled={!input.trim() || aiChat.isPending}
                    onClick={() => void send()}
                    aria-label="Send message"
                    className="h-9 w-9 shrink-0"
                >
                    {aiChat.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Send className="h-4 w-4" />
                    )}
                </Button>
            </div>
        </div>
    );
}

// Filter suggestion card
function FilterCard({
    filter,
    saved,
    saving,
    onSave
}: {
    filter: AiFilterSuggestion;
    saved: boolean;
    saving: boolean;
    onSave: () => void;
}) {
    return (
        <div className="w-full rounded-xl border bg-emerald-50 dark:bg-emerald-950/20 p-3 text-xs space-y-2">
            <div className="font-semibold text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5">
                <Filter className="h-3.5 w-3.5 shrink-0" />
                {filter.name}
            </div>
            <FilterPreview conditionsJson={filter.conditionsJson} actionsJson={filter.actionsJson} />
            <Button
                size="sm"
                variant={saved ? "outline" : "default"}
                className="w-full h-7 text-xs"
                disabled={saved || saving}
                onClick={onSave}
            >
                {saving ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                {saved ? (
                    <>
                        <Check className="mr-1 h-3 w-3" />
                        Saved
                    </>
                ) : (
                    <>
                        <Plus className="mr-1 h-3 w-3" />
                        Create Filter
                    </>
                )}
            </Button>
        </div>
    );
}

// Compose / reply draft card
function ComposeCard({
    compose,
    opened,
    onOpen
}: {
    compose: AiComposeSuggestion;
    opened: boolean;
    onOpen: () => void;
}) {
    return (
        <div className="w-full rounded-xl border bg-blue-50 dark:bg-blue-950/20 p-3 text-xs space-y-2">
            <div className="font-semibold text-blue-800 dark:text-blue-300 flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5 shrink-0" />
                {compose.subject ?? "Email draft ready"}
            </div>
            {compose.bodyHtml ? (
                <div
                    className="max-h-24 overflow-y-auto rounded border bg-background p-1.5 text-xs text-foreground/70 [&_p]:mb-1 [&_p:last-child]:mb-0"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(compose.bodyHtml) }}
                />
            ) : null}
            <Button
                size="sm"
                variant={opened ? "outline" : "default"}
                className="w-full h-7 text-xs"
                onClick={onOpen}
            >
                {opened ? (
                    <>
                        <Check className="mr-1 h-3 w-3" />
                        Opened
                    </>
                ) : (
                    <>
                        <Mail className="mr-1 h-3 w-3" />
                        Open in Compose
                    </>
                )}
            </Button>
        </div>
    );
}

function FilterPreview({
    conditionsJson,
    actionsJson
}: {
    conditionsJson: string;
    actionsJson: string;
}) {
    try {
        const conds = JSON.parse(conditionsJson) as Array<{
            field: string;
            op: string;
            value: string;
        }>;
        const acts = JSON.parse(actionsJson) as Array<{
            type: string;
            folderId?: string;
            labelId?: string;
        }>;
        return (
            <div className="space-y-0.5 text-foreground/80">
                {conds.map((c, i) => (
                    <div key={i}>
                        When <strong>{c.field}</strong> {c.op} &ldquo;{c.value}&rdquo;
                    </div>
                ))}
                {acts.map((a, i) => (
                    <div key={i}>
                        Then <strong>{a.type}</strong>
                        {a.folderId ? ` → ${a.folderId}` : ""}
                        {a.labelId ? ` → ${a.labelId}` : ""}
                    </div>
                ))}
            </div>
        );
    } catch {
        return <div className="text-foreground/50 italic">Filter rules generated</div>;
    }
}

function stripJsonBlock(text: string): string {
    return text
        .replace(/```json[\s\S]*?```/gi, "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

function bubbleText(msg: ChatMessage): string {
    const stripped = stripJsonBlock(msg.content);
    if (stripped) return stripped;
    if (msg.compose) return "Draft ready — see below.";
    if (msg.filter) return "Filter ready — see below.";
    return msg.content;
}
